import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import axiosInstance from '../../../axiosInstance';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Typography from 'apollo-react/components/Typography';
import DataTableComponent from '../../common/DataTable/PrimeDataTable';
import RadioInput from '../../common/Form/radioInput';
import { getCurrentUser } from '../../../services/authService';
import { hasRequestReviewPrivilege } from '../../../utils/adminPrivileges';
import Button from '../../common/Button';
import { exportHcpRegistryRowsForReimport } from '../../../utils/hcpRegistryImportTemplate';
import { getCalendarDaysUntilLicenseEnd } from '../../../utils/hcpLicenseRenewalDays';

/** Prime row class: red ≤30, orange ≤60, yellow ≤90 (expired counts as red). */
function expiryHighlightClass(row) {
  const days = getCalendarDaysUntilLicenseEnd(row?.license_end_date);
  if (days == null) return '';
  if (days <= 30) return 'hcp-registry-expiry-row--30';
  if (days <= 60) return 'hcp-registry-expiry-row--60';
  if (days <= 90) return 'hcp-registry-expiry-row--90';
  return '';
}

const HcpLicenseListTable = ({ persona }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessAllowed, setAccessAllowed] = useState(persona !== 'admin');
  const [expiryFilter, setExpiryFilter] = useState('all');
  const [paginationParams, setPaginationParams] = useState({
    limit: 10,
    offset: 0,
    sortField: 'createdAt',
    sortOrder: -1,
  });
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [registryTableSelection, setRegistryTableSelection] = useState([]);
  const showRegistryImport = persona === 'admin' || persona === 'super-admin';

  useEffect(() => {
    if (persona !== 'admin') {
      setAccessAllowed(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const me = await getCurrentUser();
      if (cancelled) return;
      const ok =
        me?.user?.is_super === 1 ||
        hasRequestReviewPrivilege(me?.allPrivileges || me?.privileges);
      setAccessAllowed(ok);
      if (!ok) {
        navigate('/admin/home', { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [persona, navigate]);

  useEffect(() => {
    if (!accessAllowed) return;
    fetchHcpLicenseList();
  }, [paginationParams, accessAllowed]);

  useEffect(() => {
    setPaginationParams((prev) => ({ ...prev, offset: 0 }));
  }, [expiryFilter]);

  const fetchHcpLicenseList = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/hcp-licenses-list`, {
        withCredentials: true,
      });
      setData(response?.data || []);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    if (expiryFilter === 'all') return data;
    if (expiryFilter === 'expired') {
      return data.filter((row) => {
        const days = getCalendarDaysUntilLicenseEnd(row?.license_end_date);
        return days != null && days < 0;
      });
    }
    const maxDays =
      expiryFilter === '90'
        ? 90
        : expiryFilter === '60'
          ? 60
          : expiryFilter === '30'
            ? 30
            : null;
    if (maxDays == null) return data;
    return data.filter((row) => {
      const days = getCalendarDaysUntilLicenseEnd(row?.license_end_date);
      if (days == null) return false;
      return days >= 0 && days <= maxDays;
    });
  }, [data, expiryFilter]);

  const rowClassName = useCallback((row) => expiryHighlightClass(row), []);

  const onDataTableEvent = (event) => {
    setPaginationParams((prev) => ({
      ...prev,
      limit: event.rows,
      offset: event.first,
      sortField: event.sortField || prev.sortField,
      sortOrder: event.sortOrder || prev.sortOrder,
    }));
  };

  const handleExportForReimport = () => {
    const rowsToExport =
      registryTableSelection.length > 0 ? registryTableSelection : data;
    if (!Array.isArray(rowsToExport) || rowsToExport.length === 0) {
      toast.info(t('hcpRegistryImport.exportNoRows'));
      return;
    }
    const ok = exportHcpRegistryRowsForReimport(
      rowsToExport,
      `hcp-licenses-registry-${new Date().toISOString().slice(0, 10)}`
    );
    if (ok) {
      toast.success(t('hcpRegistryImport.exportDownloaded'));
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setImporting(true);
    try {
      const { data: result } = await axiosInstance.post(
        '/api/hcp-licenses-list/import',
        formData,
        { suppressErrorToast: true }
      );
      const updated = result.updated ?? 0;
      const created = result.created ?? 0;
      if (updated > 0 || created > 0) {
        toast.success(
          result.failed > 0
            ? t('hcpRegistryImport.importPartial', {
                updated,
                created,
                failed: result.failed,
              })
            : updated > 0 && created > 0
              ? t('hcpRegistryImport.importSuccessBoth', {
                  updated,
                  created,
                })
              : created > 0 && updated === 0
                ? t('hcpRegistryImport.importSuccessCreated', {
                    count: created,
                  })
                : t('hcpRegistryImport.importSuccess', {
                    count: updated,
                  })
        );
        await fetchHcpLicenseList();
      } else if (result.failed > 0) {
        toast.warning(
          t('hcpRegistryImport.importNone', { failed: result.failed })
        );
      } else {
        toast.info(t('hcpRegistryImport.noRows'));
      }
      if (result.errors?.length) {
        const preview = result.errors
          .slice(0, 5)
          .map((err) => `Row ${err.row}: ${err.message}`)
          .join(' · ');
        toast.info(`${t('hcpRegistryImport.errorPreview')}: ${preview}`, {
          autoClose: 8000,
        });
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        t('hcpRegistryImport.importFailed');
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  const columns = [
    {
      field: 'hcp_emirates_id',
      header: 'Emirates ID',
      sortable: true,
      filter: true,
    },
    {
      field: 'hcp_name',
      header: 'Name',
      sortable: true,
      filter: true,
    },
    {
      field: 'license_number',
      header: 'License #',
      sortable: true,
      filter: true,
    },
    {
      field: 'requestProgressStatus',
      header: 'Status',
      sortable: true,
      filter: true,
      body: (rowData) => rowData?.requestProgressStatus || 'Active',
    },
    {
      field: 'license_issue_date',
      header: t('hcpRegistry.licenseIssueDateColumn'),
      sortable: true,
      filter: true,
      body: (rowData) => {
        const v = rowData?.license_issue_date;
        if (!v) return '-';
        const d = new Date(v);
        return Number.isNaN(d.getTime())
          ? String(v)
          : d.toLocaleDateString('en-GB');
      },
    },
    {
      field: 'license_end_date',
      header: t('hcpRegistry.licenseRenewalDateColumn'),
      sortable: true,
      filter: true,
      body: (rowData) => {
        const v = rowData?.license_end_date;
        if (!v) return '-';
        const d = new Date(v);
        return Number.isNaN(d.getTime())
          ? String(v)
          : d.toLocaleDateString('en-GB');
      },
    },
    {
      field: 'registry_update_source',
      header: t('hcpRegistry.lastUpdateSource'),
      sortable: true,
      filter: true,
      body: (rowData) => {
        const src = rowData?.registry_update_source;
        if (src === 'sheet_import') {
          return t('hcpRegistry.sourceSheetImport');
        }
        return t('hcpRegistry.sourceRequest');
      },
    },
    {
      field: 'updatedAt',
      header: 'Updated At',
      sortable: true,
      filter: true,
      body: (rowData) => {
        return rowData.updatedAt
          ? new Date(rowData.updatedAt).toLocaleString('en-GB')
          : '-';
      },
    },
  ];

  const actions = [
    {
      text: 'View Details',
      color: 'primary',
      onClick: (row) =>
        navigate(`/${persona}/hcp-licenses-list/details/${row.id}`),
    },
  ];

  return (
    <div>
      <style>{`
        .hcp-registry-expiry-row--90 > td { background-color: rgba(255, 249, 196, 0.225) !important; }
        .hcp-registry-expiry-row--60 > td { background-color: rgba(255, 224, 178, 0.225) !important; }
        .hcp-registry-expiry-row--30 > td { background-color: rgba(255, 205, 210, 0.225) !important; }
      `}</style>

      {showRegistryImport && accessAllowed && (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              gap: '12px',
              marginBottom: '16px',
            }}
          >
            <Button
              color="primary"
              text={t('Export to Excel')}
              style={{ minHeight: '32px', fontSize: '12px' }}
              onClick={handleExportForReimport}
              disabled={!Array.isArray(data) || data.length === 0}
            />
            <Button
              color="secondary"
              text={
                importing
                  ? t('hcpRegistryImport.importing')
                  : t('hcpRegistryImport.importExcel')
              }
              style={{
                minHeight: '32px',
                fontSize: '12px',
                backgroundColor: '#fff',
                color: '#333',
                border: '1px solid #bdbdbd',
              }}
              onClick={handleImportClick}
              disabled={importing}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              style={{ display: 'none' }}
              onChange={handleFileSelected}
            />
          </div>
          <p
            style={{
              fontSize: '12px',
              color: '#555',
              marginTop: 0,
              marginBottom: '20px',
            }}
          >
            {t('hcpRegistryImport.hint')}
          </p>
        </>
      )}

      <div style={{ marginBottom: '20px' }}>
        <Typography
          variant="bodyDefault"
          emphasis="high"
          style={{ marginBottom: '10px', display: 'block' }}
        >
          {t('hcpRegistry.expiryFilterLabel')}
        </Typography>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '20px',
            alignItems: 'center',
          }}
        >
          <RadioInput
            groupName="hcpRegistryExpiryFilter"
            checkedValue={expiryFilter}
            label={t('hcpRegistry.expiryFilterAll')}
            value="all"
            onChange={setExpiryFilter}
          />
          <RadioInput
            groupName="hcpRegistryExpiryFilter"
            checkedValue={expiryFilter}
            label={t('hcpRegistry.expiryFilter90')}
            value="90"
            onChange={setExpiryFilter}
          />
          <RadioInput
            groupName="hcpRegistryExpiryFilter"
            checkedValue={expiryFilter}
            label={t('hcpRegistry.expiryFilter60')}
            value="60"
            onChange={setExpiryFilter}
          />
          <RadioInput
            groupName="hcpRegistryExpiryFilter"
            checkedValue={expiryFilter}
            label={t('hcpRegistry.expiryFilter30')}
            value="30"
            onChange={setExpiryFilter}
          />
          <RadioInput
            groupName="hcpRegistryExpiryFilter"
            checkedValue={expiryFilter}
            label={t('hcpRegistry.expiryFilterExpired')}
            value="expired"
            onChange={setExpiryFilter}
          />
        </div>
      </div>

      <DataTableComponent
        data={filteredData}
        columns={columns}
        loading={loading}
        paginationParams={paginationParams}
        totalRecords={filteredData?.length ?? 0}
        onDataTableEvent={onDataTableEvent}
        onTableSelectionChange={setRegistryTableSelection}
        enableCreate={false}
        enablePagination={true}
        enableLazyLoading={false}
        enableExport={false}
        actions={actions}
        rowClassName={rowClassName}
        sectionName={`HCP-Licenses-List-${new Date().getDate()}-${
          new Date().getMonth() + 1
        }-${new Date().getFullYear()}-${new Date().getHours()}-${new Date().getMinutes()}`}
      />
    </div>
  );
};

export default HcpLicenseListTable;
